// imports
import { FC, useContext, useEffect } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { IconButton, Typography, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

// components
import { RenderPlanCards } from './RenderPlanCards';
import { Checkout } from './Checkout';

// interfaces, constants, helper
import { ModalCloseButton } from 'theme/styleComponent';
import { PAYMENT_DETAILS_TEXT, SELECT_PLAN_TEXT } from 'constants/constant';
import { PlanTabsEnum } from 'constants/tabs';
import { CheckoutContext } from 'contexts/CheckoutContext';
import { useFindPriceAndProductsLazyQuery } from 'generated/graphql';
import { PlanDialogProps } from 'interfaces';
import { ActionType } from 'reducer/checkoutReducer';
import { borderTop, flexCenter } from 'theme/styleConstant';

export const PlanDialog: FC<PlanDialogProps> = ({ onClose, currentPlanId, onComplete, staff, mode }) => {
  const theme = useTheme();

  const { state, dispatch } = useContext(CheckoutContext);
  const { tab, open } = state;
  const isCheckoutTab = tab === PlanTabsEnum.CHECKOUT;

  const [getPriceAndProducts, { data, loading, error }] = useFindPriceAndProductsLazyQuery();
  const { findPriceAndProducts } = data || {};
  const { prices } = findPriceAndProducts || {};

  const handleBack = () => {
    dispatch({ type: ActionType.SET_TAB, tab: PlanTabsEnum.PLANS });
  };

  const handleClose = () => {
    dispatch({ type: ActionType.RESET });
    onClose();
  };

  useEffect(() => {
    if (open) {
      getPriceAndProducts();
    }
  }, [getPriceAndProducts, open]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description">
      <DialogTitle id="alert-dialog-title">
        <Box {...flexCenter} gap={2}>
          {isCheckoutTab && (
            <IconButton onClick={handleBack}>
              <ArrowBackIcon />
            </IconButton>
          )}
          <Typography variant="h5" sx={{ width: '100%' }}>
            {isCheckoutTab ? PAYMENT_DETAILS_TEXT : SELECT_PLAN_TEXT}
          </Typography>
        </Box>
      </DialogTitle>
      <ModalCloseButton aria-label="close" onClick={handleClose}>
        <CloseIcon />
      </ModalCloseButton>
      <DialogContent sx={{ ...borderTop, p: 0 }}>
        <Box sx={{ p: 4, backgroundColor: theme.palette.secondary.main }}>
          {isCheckoutTab ? (
            <Checkout staff={staff} mode={mode} onComplete={onComplete} />
          ) : (
            <RenderPlanCards loading={loading} error={error} prices={prices} currentPlanId={currentPlanId} />
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};
